import { Check, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TASK_STATUSES } from "@/hooks/use-tasks";
import type { TaskStatus } from "@/lib/project-types";
import {
  TASK_STATUS_DOT_CLASSES,
  TASK_STATUS_LABEL_KEYS,
  TASK_STATUS_SHAPE_CLASSES,
} from "@/lib/task-status";
import { cn } from "@/lib/utils";

export interface TaskStatusButtonProps {
  status: TaskStatus;
  onStatusChange: (status: TaskStatus) => Promise<boolean> | boolean;
  /** Responsive density is controlled by the measured conversation header. */
  density?: TaskStatusButtonDensity;
}

export type TaskStatusButtonDensity = "full" | "compact" | "icon";

/** A compact task-status picker shared by task-chat headers. */
export function TaskStatusButton({
  status,
  onStatusChange,
  density = "full",
}: TaskStatusButtonProps) {
  const { t } = useTranslation();
  const statusLabel = t(TASK_STATUS_LABEL_KEYS[status]);
  const iconOnly = density === "icon";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(
            "h-8 min-w-8 shrink-0 gap-1.5",
            density === "full" && "px-3",
            density === "compact" && "px-2",
            iconOnly && "w-8 justify-center px-0",
          )}
          aria-label={t("projects:taskStatusUpdate")}
          title={t("projects:taskStatusUpdate")}
        >
          <span
            className={cn(
              "size-2 shrink-0 rounded-full",
              TASK_STATUS_DOT_CLASSES[status],
              TASK_STATUS_SHAPE_CLASSES[status],
            )}
            aria-hidden="true"
          />
          {!iconOnly && (
            <span className={cn("min-w-0 truncate", density === "compact" ? "max-w-20" : "max-w-36")}>
              {statusLabel}
            </span>
          )}
          {!iconOnly && <ChevronDown className="size-3.5 text-muted-foreground" aria-hidden="true" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" aria-label={t("projects:taskStatus")}>
        {TASK_STATUSES.map((nextStatus) => {
          const isCurrent = nextStatus === status;
          const nextStatusLabel = t(TASK_STATUS_LABEL_KEYS[nextStatus]);

          return (
            <DropdownMenuItem
              key={nextStatus}
              className={cn(isCurrent && "bg-accent font-medium")}
              aria-current={isCurrent ? "true" : undefined}
              onSelect={() => {
                if (!isCurrent) void onStatusChange(nextStatus);
              }}
            >
              <span
                className={cn(
                  "size-2 shrink-0 rounded-full",
                  TASK_STATUS_DOT_CLASSES[nextStatus],
                  TASK_STATUS_SHAPE_CLASSES[nextStatus],
                )}
                aria-hidden="true"
              />
              <span>{nextStatusLabel}</span>
              {isCurrent && <Check className="ml-auto size-3.5" aria-hidden="true" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
